<template>
  <div>
    <!-- Status Icons with Tooltips -->
    <div class="relative">
      <UserCheck
        v-if="adminVerificationStatus === 'verified'"
        class="w-5 h-5 text-green-600 hover:text-green-800 transition-colors cursor-pointer"
        @click="openDialog"
      />
      <Clock
        v-else-if="adminVerificationStatus === 'pending'"
        class="w-5 h-5 text-yellow-600 hover:text-yellow-800 transition-colors cursor-pointer"
        @click="openDialog"
      />
      <UserX
        v-else
        class="w-5 h-5 text-blue-400 hover:text-blue-600 transition-colors cursor-pointer"
        @click="openDialog"
      />
    </div>

    <!-- Modal Dialog -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="showDialog"
          class="fixed inset-0 z-50 overflow-y-auto"
          role="dialog"
        >
          <!-- Backdrop -->
          <div
            class="fixed inset-0 bg-blue-900/50 backdrop-blur-sm transition-opacity"
            @click="closeDialog"
          ></div>

          <!-- Modal Content -->
          <div class="fixed inset-0 flex items-center justify-center p-4">
            <div
              class="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative border border-blue-100"
              @click.stop
            >
              <!-- Close Button -->
              <button
                class="absolute top-4 right-4 text-blue-400 hover:text-blue-600 transition-colors"
                @click="closeDialog"
              >
                <span class="sr-only">Close</span>
                <XIcon class="w-5 h-5" />
              </button>

              <!-- Title -->
              <h2 class="text-xl font-semibold mb-6 text-blue-900">
                {{ dialogTitle }}
              </h2>

              <!-- Content based on verification status -->
              <div v-if="user">
                <!-- Pending Verification -->
                <div
                  v-if="adminVerificationStatus === 'pending'"
                  class="space-y-6"
                >
                  <div class="p-4 bg-yellow-50 rounded-lg">
                    <p class="text-yellow-800">
                      Verification code sent to
                      {{ maskPhoneNumber(adminDetails?.phone_number) }}. Code
                      expires in {{ formatTimeRemaining }}.
                    </p>
                  </div>
                  <div class="mb-6 space-y-4">
                    <div class="p-4 bg-blue-50 rounded-lg">
                      <h3 class="font-semibold text-blue-900 mb-2">
                        Verification Requirements:
                      </h3>
                      <ul class="text-blue-800 space-y-2">
                        <li class="flex items-start space-x-2">
                          <MapPin class="w-5 h-5 mt-0.5 flex-shrink-0" />
                          <span
                            >School location must match Google Maps data (within
                            {{ MAX_LOCATION_DISTANCE }}m)</span
                          >
                        </li>
                        <li class="flex items-start space-x-2">
                          <Phone class="w-5 h-5 mt-0.5 flex-shrink-0" />
                          <span
                            >Phone number must match the official number listed
                            on Google Maps</span
                          >
                        </li>
                        <li class="flex items-start space-x-2">
                          <CreditCard class="w-5 h-5 mt-0.5 flex-shrink-0" />
                          <span
                            >Verification fee: KES {{ VERIFICATION_COST }}</span
                          >
                        </li>
                      </ul>
                    </div>

                    <!-- Add location warning if coordinates are invalid -->
                    <div
                      v-if="!isValidLocation"
                      class="p-4 bg-yellow-50 rounded-lg text-yellow-800"
                    >
                      <p class="flex items-center space-x-2">
                        <AlertTriangle class="w-5 h-5 flex-shrink-0" />
                        <span
                          >Please set an accurate school location before
                          proceeding with verification.</span
                        >
                      </p>
                    </div>
                  </div>
                  <form class="space-y-4" @submit.prevent="handleVerifyCode">
                    <div class="space-y-2">
                      <label
                        class="block font-medium text-blue-900"
                        for="verificationCode"
                      >
                        Enter Verification Code
                      </label>
                      <input
                        id="verificationCode"
                        v-model="verificationCode"
                        class="w-full p-3 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                        maxlength="6"
                        placeholder="Enter 6-digit code"
                        required
                        type="text"
                      />
                    </div>

                    <button
                      :disabled="isSubmitting || !verificationCode"
                      class="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                      type="submit"
                    >
                      <span
                        v-if="isSubmitting"
                        class="flex items-center justify-center space-x-2"
                      >
                        <span
                          class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                        ></span>
                        <span>Verifying...</span>
                      </span>
                      <span v-else>Verify Code</span>
                    </button>

                    <button
                      :disabled="isSubmitting || !canResendCode"
                      class="w-full py-2 px-4 text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                      type="button"
                      @click="handleResendCode"
                    >
                      {{ resendButtonText }}
                    </button>
                  </form>
                </div>

                <!-- Initial Setup Form -->
                <form
                  v-else-if="adminVerificationStatus === 'unverified'"
                  class="space-y-6"
                  @submit.prevent="handleSubmit"
                >
                  <!-- School Phone Number -->
                  <div class="space-y-2">
                    <label
                      class="block font-medium text-blue-900"
                      for="schoolNumber"
                    >
                      School Phone Number
                    </label>
                    <input
                      id="schoolNumber"
                      v-model="formData.schoolNumber"
                      class="w-full p-3 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      pattern="^(0|254|\+254)[1-9]\d{8}$"
                      placeholder="Enter school number (e.g., 0714402822)"
                      required
                      type="tel"
                    />
                  </div>

                  <!-- Balance Warning -->
                  <div
                    v-if="balance < VERIFICATION_COST"
                    class="p-4 bg-red-50 rounded-lg text-red-800"
                  >
                    <p class="mb-2">
                      Insufficient balance for verification (KES
                      {{ VERIFICATION_COST }} required).
                    </p>
                    <p class="text-sm">Current balance: KES {{ balance }}</p>
                    <NuxtLink
                      class="mt-2 inline-block text-blue-600 hover:text-blue-800 underline"
                      to="/account"
                    >
                      Add funds to your account
                    </NuxtLink>
                  </div>

                  <!-- Submit Button -->
                  <button
                    :disabled="isSubmitting || balance < 50"
                    class="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                    type="submit"
                  >
                    <span
                      v-if="isSubmitting"
                      class="flex items-center justify-center space-x-2"
                    >
                      <span
                        class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                      ></span>
                      <span>Processing...</span>
                    </span>
                    <span v-else>Verify Phone Number</span>
                  </button>
                </form>

                <!-- Verified Status -->
                <div
                  v-else-if="adminVerificationStatus === 'verified'"
                  class="space-y-6"
                >
                  <div class="p-4 bg-green-50 rounded-lg">
                    <p class="text-green-800">
                      Phone number
                      {{ maskPhoneNumber(adminDetails?.phone_number) }} is
                      verified.
                    </p>
                  </div>

                  <button
                    class="w-full py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                    @click="handleRemoveVerification"
                  >
                    Remove Verification
                  </button>
                </div>
              </div>

              <!-- Not Logged In Message -->
              <div v-else class="p-4 bg-blue-50 rounded-lg text-blue-800">
                <p>Please log in to verify your school admin status.</p>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted } from "vue";
import {
  UserX,
  UserCheck,
  X as XIcon,
  Clock,
  AlertTriangle,
  MapPin,
  CreditCard,
  Phone,
} from "lucide-vue-next";
import { useWallet } from "~/composables/useWallet.js";
import { encode as encodeBase64 } from "js-base64";
import { useToast } from "vue-toastification";

const VERIFICATION_EXPIRY_MINUTES = 60;
const RESEND_COOLDOWN_SECONDS = 60;
const VERIFICATION_COST = 50; // KES
const MAX_LOCATION_DISTANCE = 500; // meters
const MIN_NAME_SIMILARITY = 0.8; // 80% similarity required

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // distance in meters
};
// Props and composables
const props = defineProps({
  school: {
    type: Object,
    required: true,
    validator: (value) => {
      return (
        value.id && value.name && value.location
        // &&
        // typeof value.location === 'string' &&
        // value.location.startsWith('0101000020E610')
      );
    },
  },
  withUpdate: {
    type: Boolean,
    required: false,
  },
});

const { balance, isLoading, transfer, fetchBalance } = useWallet();

// State management
const showDialog = ref(false);
const isSubmitting = ref(false);
const user = useSupabaseUser();
const supabase = useSupabaseClient();
const adminDetails = ref(null);
const verificationCode = ref("");
const resendCooldown = ref(0);
const transferTransaction = ref(null);

const formData = reactive({
  schoolNumber: "",
});

// Computed properties
const adminVerificationStatus = computed(() => {
  if (!adminDetails.value) return "unverified";
  if (adminDetails.value.phone_number_confirmed_at) return "verified";

  const updatedAt = new Date(adminDetails.value.updated_at);
  const expiryTime = new Date(
    updatedAt.getTime() + VERIFICATION_EXPIRY_MINUTES * 60000
  );

  return expiryTime > new Date() ? "pending" : "unverified";
});

const dialogTitle = computed(() => {
  switch (adminVerificationStatus.value) {
    case "verified":
      return "School Admin Status";
    case "pending":
      return "Verify Phone Number";
    default:
      return "School Admin Setup";
  }
});

const formatTimeRemaining = computed(() => {
  if (!adminDetails.value?.updated_at) return "";

  const updatedAt = new Date(adminDetails.value.updated_at);
  const expiryTime = new Date(
    updatedAt.getTime() + VERIFICATION_EXPIRY_MINUTES * 60000
  );
  const remaining = expiryTime - new Date();

  if (remaining <= 0) return "Expired";

  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);

  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
});

const canResendCode = computed(() => resendCooldown.value === 0);

const resendButtonText = computed(() => {
  return resendCooldown.value > 0
    ? `Resend code in ${resendCooldown.value}s`
    : "Resend code";
});

// Helper functions
const maskPhoneNumber = (number) => {
  if (!number) return "";
  return `${number.slice(0, 4)}****${number.slice(-3)}`;
};

const normalizePhoneNumber = (phone) => {
  const digits = phone.replace(/\D/g, "");
  if (digits.startsWith("254")) return "0" + digits.slice(3);
  if (digits.startsWith("+254")) return "0" + digits.slice(4);
  return digits;
};

const compareSchoolNames = (name1, name2) => {
  const normalizeSchoolName = (name) => {
    return name
      .toLowerCase()
      .replace(/\b(school|primary|secondary|academy|high|international)\b/g, "")
      .trim();
  };

  const normalized1 = normalizeSchoolName(name1);
  const normalized2 = normalizeSchoolName(name2);

  const maxLength = Math.max(normalized1.length, normalized2.length);
  const distance = levenshteinDistance(normalized1, normalized2);
  const similarity = (maxLength - distance) / maxLength;

  return similarity >= 0.7;
};

const levenshteinDistance = (str1, str2) => {
  const m = str1.length;
  const n = str2.length;
  const dp = Array(m + 1)
    .fill()
    .map(() => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] =
          1 +
          Math.min(
            dp[i - 1][j], // deletion
            dp[i][j - 1], // insertion
            dp[i - 1][j - 1] // substitution
          );
      }
    }
  }
  return dp[m][n];
};

const generateCode = () => {
  const code = Math.floor(100000 + Math.random() * 900000).toString();
  // Hash the code before storing
  return {
    hash: encodeBase64(code),
    plainCode: code,
  };
};

const startResendCooldown = () => {
  resendCooldown.value = RESEND_COOLDOWN_SECONDS;
  const timer = setInterval(() => {
    resendCooldown.value--;
    if (resendCooldown.value <= 0) {
      clearInterval(timer);
    }
  }, 1000);
};

// Core functions
const verifySchoolNumber = async () => {
  try {
    const { latitude, longitude } = coordinates.value;

    if (!latitude || !longitude || !props.school.name) {
      throw new Error(
        "School location data is not set. Please set a valid location first."
      );
    }

    const response = await fetch("/api/places", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        latitude,
        longitude,
        schoolName: props.school.name,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to verify school location");
    }

    const data = await response.json();

    // Find the best matching place
    let bestMatch = null;
    let minDistance = Infinity;

    for (const place of data.allPlaces) {
      // Check name similarity
      const nameSimilarity = compareSchoolNames(
        props.school.name,
        place.displayName.text
      );
      if (nameSimilarity < MIN_NAME_SIMILARITY) continue;

      // Calculate distance
      const distance = calculateDistance(
        latitude,
        longitude,
        place.location.latitude,
        place.location.longitude
      );

      // Update best match if this is closer
      if (distance < minDistance) {
        minDistance = distance;
        bestMatch = place;
      }
    }

    // Verify location and phone number
    if (!bestMatch || minDistance > MAX_LOCATION_DISTANCE) {
      throw new Error(
        "School location does not match Google Maps data. " +
          "Please ensure the school's location is accurately set."
      );
    }

    if (!bestMatch.internationalPhoneNumber) {
      throw new Error(
        "No phone number found for this school on Google Maps. " +
          "Please ensure the school has a registered phone number on Google Maps."
      );
    }

    const phoneMatches =
      normalizePhoneNumber(bestMatch.internationalPhoneNumber) ===
      normalizePhoneNumber(formData.schoolNumber);

    if (!phoneMatches) {
      throw new Error(
        "The provided phone number does not match the official school contact number on Google Maps. " +
          "Please use the official school contact number."
      );
    }
    await updateSchool();
    return true;
  } catch (error) {
    console.error("School verification error:", error);
    throw error;
  }
};

const updateSchool = async () => {
  if (props.withUpdate) {
    try {
      let school = props.school;
      let schoolLocation = school.location;
      school.location = `POINT(${schoolLocation.lng} ${schoolLocation.lat})`;
      const { data: updateData, error: updateError } = await supabase
        .from("schools")
        .update(school)
        .eq("code", school.code)
        .select()
        .single();

      if (updateData) {
        try {
          const { error: deleteError } = await supabase
            .from("school_proposed_data_changes")
            .delete()
            .eq("school_id", school.id);

          if (deleteError) {
            console.error("Failed to delete proposed changes:", deleteError);
          }
        } catch (deleteErr) {
          console.error("Error while deleting proposed changes:", deleteErr);
        }
      }

      if (updateError) {
        console.error("Failed to update school:", updateError);
      }
    } catch (err) {
      console.error("Error during school update process:", err);
    }
  }
};

const saveAdminDetails = async (transferId, verificationData) => {
  const { data, error } = await supabase
    .from("school_admins")
    .upsert(
      {
        user_id: user.value?.id,
        school_id: props.school.id,
        transaction_id: transferId,
        phone_number: formData.schoolNumber,
        phone_number_confirmation_code: verificationData.hash,
        updated_at: new Date().toISOString(),
      },
      {
        onConflict: "user_id,school_id",
        ignoreDuplicates: false,
      }
    )
    .select();

  if (error) {
    console.error(error);
    throw new Error("Failed to save admin details");
  }

  try {
    await sendVerificationCode(
      "254722136702",
      // '254' + normalizePhoneNumber(formData.schoolNumber).slice(-9),
      verificationData.plainCode
    );
  } catch (error) {
    console.error("Error sending verification code:", error);
    throw new Error("Failed to send verification code");
  }

  return data;
};
const toast = useToast();

const handleVerifyCode = async () => {
  if (!verificationCode.value || !adminDetails.value) return;

  isSubmitting.value = true;

  try {
    // Check if the verification is expired
    const updatedAt = new Date(adminDetails.value.updated_at);
    const expiryTime = new Date(
      updatedAt.getTime() + VERIFICATION_EXPIRY_MINUTES * 60000
    );

    if (expiryTime < new Date()) {
      throw new Error(
        "Verification code has expired. Please request a new code."
      );
    }

    // Compare the hashed codes
    const inputHash = encodeBase64(verificationCode.value);
    if (inputHash !== adminDetails.value.phone_number_confirmation_code) {
      throw new Error("Invalid verification code");
    }

    // Update the verification status
    const { error } = await supabase
      .from("school_admins")
      .update({
        phone_number_confirmed_at: new Date().toISOString(),
      })
      .eq("id", adminDetails.value.id);

    if (error) throw error;

    await getAdminDetails();
    toast.success("Phone number verified successfully!");

    verificationCode.value = "";
  } catch (error) {
    toast.error(error.message || "Failed to verify code");
  } finally {
    isSubmitting.value = false;
  }
};

const handleResendCode = async () => {
  if (!canResendCode.value || !adminDetails.value) return;

  isSubmitting.value = true;

  try {
    const verificationData = generateCode();
    const { error } = await supabase
      .from("school_admins")
      .update({
        phone_number_confirmation_code: verificationData.hash,
        updated_at: new Date().toISOString(),
      })
      .eq("id", adminDetails.value.id);

    if (error) throw error;

    await sendVerificationCode(
      "254" + normalizePhoneNumber(adminDetails.value.phone_number).slice(-9),
      verificationData.plainCode
    );
    await getAdminDetails();

    startResendCooldown();
    alert("New verification code sent!");
  } catch (error) {
    console.error("Resend error:", error);
    alert(error.message || "Failed to resend verification code");
  } finally {
    isSubmitting.value = false;
  }
};

const handleRemoveVerification = async () => {
  if (!adminDetails.value) return;

  if (
    !confirm(
      "Are you sure you want to remove your verification? This action cannot be undone."
    )
  ) {
    return;
  }

  isSubmitting.value = true;

  try {
    const { error } = await supabase
      .from("school_admins")
      .delete()
      .eq("id", adminDetails.value.id);

    if (error) throw error;

    adminDetails.value = null;
    closeDialog();
  } catch (error) {
    console.error("Remove verification error:", error);
    alert(error.message || "Failed to remove verification");
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  if (!user.value?.id) {
    alert("Please login to continue");
    return;
  }

  if (!isValidLocation.value) {
    alert(
      "Please set an accurate school location before proceeding with verification."
    );
    return;
  }

  if (balance.value < VERIFICATION_COST) {
    alert(
      `Insufficient balance. Verification requires KES ${VERIFICATION_COST}.`
    );
    return;
  }

  isSubmitting.value = true;

  try {
    const isVerified = await verifySchoolNumber();
    if (!isVerified) {
      throw new Error(
        "School verification failed. Please ensure:\n\n" +
          "1. The school location is accurately set\n" +
          "2. The phone number matches the official school contact\n" +
          "3. The school name matches Google Maps data"
      );
    }

    let tt = await transfer(
      user.value.id,
      "cybervant.ke@gmail.com",
      VERIFICATION_COST,
      supabase
    );
    transferTransaction.value = tt[0];

    const verificationData = generateCode();
    await saveAdminDetails(transferTransaction.value.id, verificationData);
    await getAdminDetails();

    startResendCooldown();
  } catch (error) {
    console.error("Setup error:", error);
    alert(error.message || "An error occurred during setup");
  } finally {
    isSubmitting.value = false;
  }
};
// Database interaction
const getAdminDetails = async () => {
  if (!user.value?.id) return;

  const { data, error } = await supabase
    .from("school_admins")
    .select("*")
    .eq("user_id", user.value.id)
    .eq("school_id", props.school.id)
    .maybeSingle();

  if (error) {
    console.error("Error fetching admin details:", error);
    return;
  }

  adminDetails.value = data;
};

// Coordinates computation
const coordinates = computed(() => {
  const location = props.school.location;
  try {
    if (
      location &&
      typeof location === "object" &&
      "lat" in location &&
      "lng" in location
    ) {
      return {
        latitude: location.lat,
        longitude: location.lng,
      };
    }

    const coordPart = props.school.location.substring(18);
    const x = coordPart.substring(0, 16);
    const y = coordPart.substring(16, 32);

    const buf1 = new ArrayBuffer(8);
    const buf2 = new ArrayBuffer(8);
    const view1 = new DataView(buf1);
    const view2 = new DataView(buf2);

    for (let i = 0; i < 8; i++) {
      view1.setUint8(i, parseInt(x.substring(14 - i * 2, 16 - i * 2), 16));
      view2.setUint8(i, parseInt(y.substring(14 - i * 2, 16 - i * 2), 16));
    }

    return {
      longitude: view1.getFloat64(0),
      latitude: view2.getFloat64(0),
    };
  } catch (error) {
    console.error("Error parsing coordinates:", error);
    return { latitude: 0, longitude: 0 };
  }
});

// Dialog management
const openDialog = () => {
  showDialog.value = true;
};

const closeDialog = () => {
  showDialog.value = false;
  resetForm();
};

const resetForm = () => {
  formData.schoolNumber = "";
  verificationCode.value = "";
  isSubmitting.value = false;
};

// Lifecycle hooks
let refreshInterval;
let supabaseChannel;

onMounted(async () => {
  if (user.value) {
    await Promise.all([
      fetchBalance(user.value.id, supabase),
      getAdminDetails(),
    ]);

    // Set up real-time updates
    supabaseChannel = supabase
      .channel("admin-updates-gvgkjhllh")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "school_admins",
          filter: `user_id=eq.${user.value.id}`,
        },
        async (payload) => {
          await getAdminDetails();
        }
      )
      .subscribe();

    // Refresh admin details periodically to check expiry
    refreshInterval = setInterval(async () => {
      if (adminVerificationStatus.value === "pending") {
        await getAdminDetails();
      }
    }, 30000); // Every 30 seconds
  }
});

onUnmounted(() => {
  if (supabaseChannel) {
    supabaseChannel.unsubscribe();
  }
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
});

// Watch for user changes
watch(user, async (newUser) => {
  if (newUser) {
    await Promise.all([fetchBalance(newUser.id, supabase), getAdminDetails()]);
  } else {
    adminDetails.value = null;
  }
});

const isValidLocation = computed(() => {
  const { latitude, longitude } = coordinates.value;
  return (
    latitude !== 0 &&
    longitude !== 0 &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
});
</script>
